import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Bot, Send, Loader2, RotateCcw, User as UserIcon, Sparkles } from "lucide-react";

const ALL = "__all__";

const renderContent = (text) =>
  text.split("\n").map((line, li) => {
    if (line.trim() === "---" || line.trim() === "***") return <hr key={li} className="my-2 border-border" />;
    const heading = line.match(/^#{1,4}\s+(.*)$/);
    if (heading) return <span key={li}><strong>{heading[1].replace(/\*\*/g, "")}</strong>{"\n"}</span>;
    const segs = line.split(/(\*\*[^*]+\*\*)/g).map((s, i) =>
      s.startsWith("**") && s.endsWith("**")
        ? <strong key={i}>{s.slice(2, -2)}</strong>
        : s.replace(/(^|[^*])\*([^*]+)\*/g, "$1$2")
    );
    return <span key={li}>{segs}{"\n"}</span>;
  });

const SUGGESTIONS = [
  "Riassumi lo stato dei miei clienti",
  "Quale cliente ha più gap di conformità NIS2?",
  "Cosa dovrei proporre come prossimo passo commerciale?",
];

export default function AssistantPage() {
  const { clients } = useClient();
  const [contextId, setContextId] = useState(ALL);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const resetChat = (ctx = contextId) => {
    setMessages([]);
    setSessionId(crypto.randomUUID());
    setContextId(ctx);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: msg,
          session_id: sessionId,
          client_id: contextId === ALL ? null : contextId,
        }),
      });
      if (!res.ok) {
        let detail = "Errore dell'assistente";
        try { detail = (await res.json()).detail || detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let gotText = false;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.error) throw new Error(ev.error);
          if (ev.delta) {
            gotText = true;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + ev.delta };
              return copy;
            });
          }
        }
      }
      if (!gotText) throw new Error("Nessuna risposta ricevuta. Riprova.");
    } catch (e) {
      toast.error(e.message || "Errore dell'assistente");
      setMessages((m) => (m[m.length - 1]?.content === "" ? m.slice(0, -2) : m));
    } finally {
      setStreaming(false);
    }
  };

  const ctxLabel = contextId === ALL ? "tutto lo studio" : clients.find((c) => c.id === contextId)?.name;

  return (
    <Layout>
      <PageHeader title="Assistente AI" subtitle="Chiedi qualsiasi cosa sui tuoi clienti e assessment">
        <Select value={contextId} onValueChange={(v) => resetChat(v)}>
          <SelectTrigger className="rounded-none h-9 w-56" data-testid="assistant-context-select"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value={ALL}>Tutto lo studio</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-none" data-testid="new-chat-btn" onClick={() => resetChat()} disabled={streaming}>
          <RotateCcw className="w-4 h-4 mr-2" /> Nuova chat
        </Button>
      </PageHeader>

      <div className="p-4 sm:p-8 max-w-4xl mx-auto flex flex-col" data-testid="assistant-page" style={{ minHeight: "calc(100vh - 8rem)" }}>
        <div className="flex-1 space-y-4">
          {messages.length === 0 && (
            <div className="border border-dashed border-border bg-white p-8 text-center" data-testid="assistant-empty">
              <div className="w-12 h-12 bg-primary mx-auto flex items-center justify-center"><Bot className="w-6 h-6 text-primary-foreground" /></div>
              <h2 className="font-heading font-black text-lg tracking-tight mt-4">Il tuo consulente AI</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Rispondo usando i dati reali di <span className="font-medium text-foreground">{ctxLabel}</span>: readiness, conformità, pipeline e proposte.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-5">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} data-testid={`assistant-suggestion-${i}`} onClick={() => send(s)}
                    className="text-xs border border-border px-3 py-1.5 hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`assistant-msg-${i}`}>
              {m.role === "assistant" && (
                <div className="w-8 h-8 bg-primary flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-primary-foreground" /></div>
              )}
              <div className={`max-w-[80%] px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === "user" ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}>
                {m.content
                  ? (m.role === "assistant" ? renderContent(m.content) : m.content)
                  : (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : "")}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 bg-secondary flex items-center justify-center shrink-0"><UserIcon className="w-4 h-4 text-muted-foreground" /></div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="sticky bottom-0 mt-6 bg-white border border-border p-2 flex items-center gap-2"
        >
          <input
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Chiedi qualcosa su ${ctxLabel}…`}
            className="flex-1 px-3 py-2 text-sm outline-none bg-transparent"
            disabled={streaming}
          />
          <Button type="submit" className="rounded-none" data-testid="assistant-send-btn" disabled={streaming || !input.trim()}>
            {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
